import React, { useState, useEffect } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, 
  faExclamationTriangle, 
  faRocket,
  faFlask,
  faCode,
  faChartLine,
  faClipboardList,
  faArrowUpRightFromSquare,
  faUsers,
  faMicrochip,
} from '@fortawesome/free-solid-svg-icons';
import OtaStatusChart from './OtaStatusChart';
import DeviceActivityChart from './DeviceActivityChart';
import FirmwareVersionChart from './FirmwareVersionChart';
import DeviceLifecycleSection from './DeviceLifecycleSection';
import UsersDashboard from './UsersDashboard';
import FirmwareVersionHistory from './FirmwareVersionHistory';
import FirmwareUpdatesChart from './FirmwareUpdatesChart';
import SurveyDashboardPage from '../views/SurveyDashboardPage';
import { dashboardUrl } from '../../config/hosts';

interface DeviceStats {
  totalDevices: number;
  devicesMarkedForUpdate: number;
  otaNone?: number;
  otaOptIn?: number;
  otaForce?: number;
  myDevicesCount: number;
  devicesByType: {
    prod: number;
    beta: number;
    dev: number;
    deco: number;
  };
  recentlyUpdated: number;
  offlineDevices: number;
}

interface DeviceDashboardProps {
  apiBaseUrl: string;
  showSurveyTab?: boolean;
}

const DASHBOARD_TABS = ['firmware', 'users', 'metrics', 'survey'] as const;

const tabLinkClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link${isActive ? ' active' : ''}`;

const DeviceDashboard: React.FC<DeviceDashboardProps> = ({ apiBaseUrl, showSurveyTab = false }) => {
  const { tab } = useParams();
  const activeTab = DASHBOARD_TABS.includes(tab as (typeof DASHBOARD_TABS)[number])
    ? tab
    : 'firmware';
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDeviceStats();
  }, []);

  const fetchDeviceStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/metrics`, {
        withCredentials: true
      });
      setStats(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching device statistics:', error);
      setError('Failed to fetch device statistics');
      
      // Use mock data for now (in a real app, you would handle the error differently)
      setStats({
        totalDevices: 5294,
        devicesMarkedForUpdate: 641,
        otaNone: 4653,
        otaOptIn: 641,
        otaForce: 0,
        myDevicesCount: 12,
        devicesByType: {
          prod: 5294,
          beta: 245,
          dev: 124,
          deco: 0
        },
        recentlyUpdated: 152,
        offlineDevices: 47
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return <div className="alert alert-danger">{error}</div>;
  }

  // Safety check - if somehow stats is still null
  if (!stats) {
    return <div className="alert alert-warning">No device statistics available</div>;
  }

  if (tab === 'survey' && !showSurveyTab) {
    return <Navigate to="/dashboard/firmware" replace />;
  }

  if (tab && tab !== activeTab) {
    return <Navigate to="/dashboard/firmware" replace />;
  }

  return (
    <div>
      <div className="row mb-4">
        <div className="col-12 col-sm-6 col-xl mb-3">
          <div className="dashboard-stat-card card text-white bg-primary h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title">Total Devices</h6>
                  <h2 className="mb-0">{stats.totalDevices.toLocaleString()}</h2>
                </div>
                <FontAwesomeIcon icon={faGlobe} size="3x" className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-xl mb-3">
          <div className="dashboard-stat-card card text-white bg-success h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title">Production</h6>
                  <h2 className="mb-0">{stats.devicesByType.prod.toLocaleString()}</h2>
                </div>
                <FontAwesomeIcon icon={faRocket} size="3x" className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-xl mb-3">
          <div className="dashboard-stat-card card text-white bg-info h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title">Beta</h6>
                  <h2 className="mb-0">{stats.devicesByType.beta.toLocaleString()}</h2>
                </div>
                <FontAwesomeIcon icon={faFlask} size="3x" className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-xl mb-3">
          <div className="dashboard-stat-card card text-white bg-warning h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title">Development</h6>
                  <h2 className="mb-0">{stats.devicesByType.dev.toLocaleString()}</h2>
                </div>
                <FontAwesomeIcon icon={faCode} size="3x" className="opacity-50" />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl mb-3">
          <div className="dashboard-stat-card card text-white bg-danger h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title">Decommissioned</h6>
                  <h2 className="mb-0">{stats.devicesByType.deco?.toLocaleString?.() ?? 0}</h2>
                </div>
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
        <ul className="nav nav-tabs nav-tabs-sm flex-nowrap overflow-auto" style={{ borderBottom: '1px solid #dee2e6' }}>
          <li className="nav-item">
            <NavLink to="/dashboard/firmware" className={tabLinkClass} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
              <FontAwesomeIcon icon={faMicrochip} className="me-1" />
              Devices
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/dashboard/users" className={tabLinkClass} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              Users
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/dashboard/metrics" className={tabLinkClass} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
              <FontAwesomeIcon icon={faChartLine} className="me-1" />
              Metrics
            </NavLink>
          </li>
          {showSurveyTab && (
            <li className="nav-item">
              <NavLink to="/dashboard/survey" className={tabLinkClass} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
              <FontAwesomeIcon icon={faClipboardList} className="me-1" />
              Surveys
            </NavLink>
            </li>
          )}
        </ul>
        <a
          href={dashboardUrl('/')}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline-primary btn-sm"
        >
          Open Full Dashboard
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2" />
        </a>
      </div>

      {activeTab === 'firmware' && (
        <>
          <DeviceLifecycleSection apiBaseUrl={apiBaseUrl} />
          <div className="row mb-4">
            <div className="col-12">
              <FirmwareUpdatesChart apiBaseUrl={apiBaseUrl} />
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12">
              <OtaStatusChart
                apiBaseUrl={apiBaseUrl}
                totalDevices={stats.totalDevices}
                otaNone={stats.otaNone ?? Math.max(stats.totalDevices - stats.devicesMarkedForUpdate, 0)}
                otaOptIn={stats.otaOptIn ?? stats.devicesMarkedForUpdate}
                otaForce={stats.otaForce ?? 0}
              />
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12">
              <FirmwareVersionHistory apiBaseUrl={apiBaseUrl} />
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12">
              <FirmwareVersionChart apiBaseUrl={apiBaseUrl} />
            </div>
          </div>
        </>
      )}

      {activeTab === 'metrics' && (
        <>
          <div className="row mb-4">
            <div className="col-12">
              <DeviceActivityChart apiBaseUrl={apiBaseUrl} />
            </div>
          </div>
        </>
      )}

      {activeTab === 'users' && <UsersDashboard apiBaseUrl={apiBaseUrl} />}

      {activeTab === 'survey' && showSurveyTab && <SurveyDashboardPage />}
    </div>
  );
};

export default DeviceDashboard; 